import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  useApi,
  ApiError,
  type AssessmentSessionRequest,
  type AssessmentSessionResponse,
  type CompletionMode,
  type CycleLabel,
  type SubdomainAssessmentRow,
  type Supervisor,
  type SupervisorListResponse,
  type WhanauProfileResponse,
  type WizardDraftResponse,
} from "../api/client";
import {
  CYCLE_LABELS,
  FACTOR_STATES,
  NOT_ASSESSED_REASONS,
  frameworkLabel,
  useFramework,
  type DomainId,
  type FactorStateKind,
  type MergedDomain,
  type NotAssessedReason,
} from "../framework";
import { StateIcon } from "./StateIcon";
import { useSubmissionState } from "./useSubmissionState";
import { ExitSurveyQr } from "./ExitSurveyQr";
import "./AssessmentWizard.css";

/**
 * AssessmentWizard — modal overlay over the whānau profile.
 *
 * Step-by-step session-recording flow rendered as a centred modal with a
 * blurred backdrop. Step 0 is setup (cycle, date, setting, kaimahi notes);
 * steps 1..6 are one card per framework domain.
 *
 * Each subdomain pre-populates from the latest current state, so a
 * midpoint session starts where the previous session left off.
 *
 * Dismissal:
 *   - ESC key (confirms first if dirty)
 *   - Backdrop click (confirms first if dirty)
 *   - "Close" button (confirms first if dirty)
 *
 * Posts via POST /api/v1/whanau/:id/assessment-sessions. On success the
 * onSaved callback fires (parent uses it to refetch the profile) and the
 * modal closes.
 *
 * Rendered via createPortal so the overlay sits at document.body level and
 * isn't constrained by parent positioning / overflow.
 */

type PickState = {
  state_kind: FactorStateKind;
  not_assessed_reason_id: NotAssessedReason | null;
  /** Single note field; visibility decided by share_with_whanau. */
  note: string;
  /** When true the note is whānau-visible; when false it's kaimahi-private. */
  share_with_whanau: boolean;
};

type StepKind = { kind: "setup" } | { kind: "domain"; domain: MergedDomain };

type Props = {
  whanauId: number;
  profile: WhanauProfileResponse;
  onClose: () => void;
  onSaved: () => void;
};

export function AssessmentWizard({
  whanauId,
  profile,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const callApi = useApi();
  const { domains, isHidden } = useFramework();

  // ---- Submission rules from session history ------------------------------
  // Server enforces the same rules; this mirrors them in the UX so the
  // kaimahi can't pick a cycle they aren't actually allowed to submit.
  const submissionState = useSubmissionState(profile.sessions);

  // ---- Wizard state -------------------------------------------------------
  const [cycleLabel, setCycleLabel] = useState<CycleLabel>(
    submissionState.defaultCycle,
  );
  // Who completed the assessment (ADR-012). Empty = not yet chosen; required
  // before leaving the setup step.
  const [completedBy, setCompletedBy] = useState<CompletionMode | "">("");
  const [sessionAt, setSessionAt] = useState<string>(() => isoLocalNow());
  const [setting, setSetting] = useState<string>("");
  const [sessionNotes, setSessionNotes] = useState<string>("");
  // Selected supervisor (ADR-013) — required before leaving the setup step.
  const [supervisorOid, setSupervisorOid] = useState<string>("");
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  // Seed picks from the latest current state per subdomain. State + reason
  // carry forward so a midpoint session starts where the entry left off;
  // notes are NOT carried (each session has its own notes per ADR-007 §2.2).
  const [picks, setPicks] = useState<ReadonlyMap<string, PickState>>(() => {
    const seed = new Map<string, PickState>();
    for (const a of profile.assessments) {
      const state_kind =
        (a.state_kind as FactorStateKind | null) ?? "not-assessed";
      const reason =
        state_kind === "not-assessed"
          ? ((a.not_assessed_reason_id as NotAssessedReason | null) ??
            "not-yet-assessed")
          : null;
      seed.set(a.subdomain_id, {
        state_kind,
        not_assessed_reason_id: reason,
        note: "",
        share_with_whanau: false,
      });
    }
    return seed;
  });
  const [touched, setTouched] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [saveState, setSaveState] = useState<
    | { kind: "idle" }
    | { kind: "saving" }
    | { kind: "saved"; sessionId: number; count: number }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  // ---- Draft state --------------------------------------------------------
  // Separate from saveState so the Save-draft button has its own
  // affordance and doesn't fight with the Submit flow.
  const [draftState, setDraftState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "restored"; at: string }
    | { kind: "saving-draft" }
    | { kind: "saved-draft" }
    | { kind: "draft-error"; message: string }
  >({ kind: "loading" });

  const steps: readonly StepKind[] = useMemo(
    () => [
      { kind: "setup" },
      ...domains.map((d): StepKind => ({ kind: "domain", domain: d })),
    ],
    [domains],
  );
  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // ---- Pick handling ------------------------------------------------------
  const setPick = useCallback(
    (subdomainId: string, partial: Partial<PickState>) => {
      setTouched(true);
      setPicks((prev) => {
        const next = new Map(prev);
        const existing = prev.get(subdomainId) ?? {
          state_kind: "not-assessed" as FactorStateKind,
          not_assessed_reason_id: null as NotAssessedReason | null,
          note: "",
          share_with_whanau: false,
        };
        const merged: PickState = { ...existing, ...partial };
        if (merged.state_kind !== "not-assessed") {
          merged.not_assessed_reason_id = null;
        }
        next.set(subdomainId, merged);
        return next;
      });
      setSaveState((s) => (s.kind === "saved" ? { kind: "idle" } : s));
      // A typed pick invalidates any "saved-draft" toast — user has more to save.
      setDraftState((s) => (s.kind === "saved-draft" ? { kind: "idle" } : s));
    },
    [],
  );

  // ---- Draft load on open -------------------------------------------------
  // Fetch any existing draft for this (kaimahi, whanau) pair. If present,
  // hydrate the wizard's pick map + setup fields from it. Best-effort —
  // a 204 / 404 / network blip just leaves the wizard with the seeded
  // current-state picks, which is the existing behaviour.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<WizardDraftResponse | null>(
          `/whanau/${String(whanauId)}/wizard-draft`,
        );
        if (cancelled) return;
        if (!data || data.draft === null || typeof data.draft !== "object") {
          setDraftState({ kind: "idle" });
          return;
        }
        const d = data.draft as {
          cycle_label?: unknown;
          session_at?: unknown;
          setting?: unknown;
          session_notes?: unknown;
          supervisor_email?: unknown;
          current_step_index?: unknown;
          picks?: unknown;
        };
        // Hydrate scalar fields (only when shape matches expectations).
        if (
          typeof d.cycle_label === "string" &&
          (CYCLE_LABELS as readonly unknown[]).includes(d.cycle_label)
        ) {
          setCycleLabel(d.cycle_label as CycleLabel);
        }
        if (typeof d.session_at === "string" && d.session_at) {
          setSessionAt(d.session_at);
        }
        if (typeof d.setting === "string") setSetting(d.setting);
        if (typeof d.session_notes === "string")
          setSessionNotes(d.session_notes);
        const draftSupervisor = (d as { supervisor_oid?: unknown })
          .supervisor_oid;
        if (typeof draftSupervisor === "string") {
          setSupervisorOid(draftSupervisor);
        }
        const draftCompletedBy = (d as { completed_by?: unknown }).completed_by;
        if (
          typeof draftCompletedBy === "string" &&
          ["kaimahi", "whanau", "collaborative"].includes(draftCompletedBy)
        ) {
          setCompletedBy(draftCompletedBy as CompletionMode);
        }
        if (
          typeof d.current_step_index === "number" &&
          Number.isFinite(d.current_step_index) &&
          d.current_step_index >= 0
        ) {
          setCurrentStepIndex(Math.floor(d.current_step_index));
        }
        // Hydrate picks — array of { subdomain_id, state_kind, reason, note, share }.
        if (Array.isArray(d.picks)) {
          setPicks((seed) => {
            const next = new Map(seed);
            for (const raw of d.picks as unknown[]) {
              if (!raw || typeof raw !== "object") continue;
              const p = raw as {
                subdomain_id?: unknown;
                state_kind?: unknown;
                not_assessed_reason_id?: unknown;
                note?: unknown;
                share_with_whanau?: unknown;
              };
              if (typeof p.subdomain_id !== "string") continue;
              const stateKind =
                typeof p.state_kind === "string" ? p.state_kind : null;
              if (
                stateKind === null ||
                !["risk", "change", "protective", "not-assessed"].includes(
                  stateKind,
                )
              ) {
                continue;
              }
              next.set(p.subdomain_id, {
                state_kind: stateKind as FactorStateKind,
                not_assessed_reason_id:
                  stateKind === "not-assessed" &&
                  typeof p.not_assessed_reason_id === "string"
                    ? (p.not_assessed_reason_id as NotAssessedReason)
                    : null,
                note: typeof p.note === "string" ? p.note : "",
                share_with_whanau: p.share_with_whanau === true,
              });
            }
            return next;
          });
        }
        setTouched(false);
        setDraftState({ kind: "restored", at: data.updated_at });
      } catch (err) {
        if (cancelled) return;
        // 204 No Content surfaces as a 204 ApiError in some implementations;
        // either way no draft → idle.
        if (
          err instanceof ApiError &&
          (err.status === 204 || err.status === 404)
        ) {
          setDraftState({ kind: "idle" });
        } else {
          const message =
            err instanceof Error ? err.message : "Failed to load draft";

          console.warn("[wizard] draft load failed:", message);
          setDraftState({ kind: "idle" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // We deliberately load once per (wizard, whanau) mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whanauId]);

  // Load the supervisor dropdown (ADR-013) once per mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await callApi<SupervisorListResponse>("/supervisors");
        if (!cancelled) setSupervisors(res.rows);
      } catch {
        if (!cancelled) setSupervisors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Save-draft handler -------------------------------------------------
  const onSaveDraft = useCallback(async () => {
    setDraftState({ kind: "saving-draft" });
    try {
      const draft = {
        cycle_label: cycleLabel,
        completed_by: completedBy,
        session_at: sessionAt,
        setting,
        session_notes: sessionNotes,
        supervisor_oid: supervisorOid,
        current_step_index: currentStepIndex,
        picks: Array.from(picks.entries()).map(([subdomain_id, p]) => ({
          subdomain_id,
          state_kind: p.state_kind,
          not_assessed_reason_id: p.not_assessed_reason_id,
          note: p.note,
          share_with_whanau: p.share_with_whanau,
        })),
      };
      await callApi(`/whanau/${String(whanauId)}/wizard-draft`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ draft }),
      });
      setDraftState({ kind: "saved-draft" });
      setTouched(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${String(err.status)} — ${err.message}`
          : err instanceof Error
            ? err.message
            : "Failed to save draft";
      setDraftState({ kind: "draft-error", message });
    }
  }, [
    whanauId,
    cycleLabel,
    completedBy,
    sessionAt,
    setting,
    sessionNotes,
    supervisorOid,
    currentStepIndex,
    picks,
    callApi,
  ]);

  // ---- Subdomain grouping + completeness ---------------------------------
  // Filter out subdomains hidden by admin override so the wizard doesn't
  // offer them as new picks. Historical data for hidden subdomains still
  // shows on the profile (with a "Retired" chip), but a new session
  // shouldn't add fresh entries against them.
  const subdomainsByDomain = useMemo(() => {
    const m = new Map<DomainId, SubdomainAssessmentRow[]>();
    for (const d of domains) m.set(d.id, []);
    for (const a of profile.assessments) {
      if (isHidden("subdomain", a.subdomain_id)) continue;
      const list = m.get(a.domain_id as DomainId);
      if (list) list.push(a);
    }
    return m;
  }, [profile, domains, isHidden]);

  const isPickComplete = (p: PickState | undefined): boolean => {
    if (!p) return false;
    if (p.state_kind === "not-assessed") return !!p.not_assessed_reason_id;
    return true;
  };

  const currentStepComplete = useMemo(() => {
    if (currentStep.kind === "setup")
      return completedBy !== "" && supervisorOid !== "";
    const rows = subdomainsByDomain.get(currentStep.domain.id) ?? [];
    return rows.every((a) => isPickComplete(picks.get(a.subdomain_id)));
  }, [currentStep, subdomainsByDomain, picks, completedBy, supervisorOid]);

  const totalSubdomains = profile.assessments.length;
  const completeCount = useMemo(() => {
    let n = 0;
    for (const a of profile.assessments) {
      if (isPickComplete(picks.get(a.subdomain_id))) n++;
    }
    return n;
  }, [profile, picks]);

  const completedSteps = useMemo(() => {
    const s = new Set<number>();
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (step.kind === "setup") {
        if (completedBy !== "" && supervisorOid !== "") s.add(i);
      } else {
        const rows = subdomainsByDomain.get(step.domain.id) ?? [];
        if (
          rows.length > 0 &&
          rows.every((a) => isPickComplete(picks.get(a.subdomain_id)))
        ) {
          s.add(i);
        }
      }
    }
    return s;
  }, [steps, subdomainsByDomain, picks, completedBy, supervisorOid]);

  // ---- Dismissal handling ------------------------------------------------
  const isDirty =
    touched && saveState.kind !== "saving" && saveState.kind !== "saved";

  const requestClose = useCallback(() => {
    if (isDirty) {
      const proceed = window.confirm(t("wizard.discardConfirm"));
      if (!proceed) return;
    }
    onClose();
  }, [isDirty, onClose, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [requestClose]);

  // beforeunload still protects against tab close / reload.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [isDirty]);

  // Lock body scroll while modal is mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ---- Submit -------------------------------------------------------------
  const onSubmit = useCallback(async () => {
    const allComplete = profile.assessments.every((a) =>
      isPickComplete(picks.get(a.subdomain_id)),
    );
    if (!allComplete) return;
    // completed_by is required (ADR-012); the setup step blocks progress until
    // it's chosen, so this is a belt-and-braces guard.
    if (completedBy === "") return;
    if (supervisorOid === "") return;

    setSaveState({ kind: "saving" });
    try {
      const body: AssessmentSessionRequest = {
        cycle_label: cycleLabel,
        completed_by: completedBy,
        session_at: new Date(sessionAt).toISOString(),
        setting: setting.trim() ? setting.trim() : null,
        session_notes: sessionNotes.trim() ? sessionNotes.trim() : null,
        shared_summary: null,
        supervisor_oid: supervisorOid,
        assessments: profile.assessments.map((a) => {
          const p: PickState = picks.get(a.subdomain_id) ?? {
            state_kind: "not-assessed",
            not_assessed_reason_id: "not-yet-assessed",
            note: "",
            share_with_whanau: false,
          };
          // The single note field is routed to one of two columns by the
          // share_with_whanau flag. ADR-007 §2.2 Option B (Sue-confirmed):
          //   share_with_whanau = true  → shared_notes (whānau-visible)
          //   share_with_whanau = false → kaimahi_notes (private)
          // Both null when the note is empty.
          const noteText = p.note.trim();
          return {
            subdomain_id: a.subdomain_id,
            state_kind: p.state_kind,
            not_assessed_reason_id:
              p.state_kind === "not-assessed" ? p.not_assessed_reason_id : null,
            kaimahi_notes: noteText && !p.share_with_whanau ? noteText : null,
            shared_notes: noteText && p.share_with_whanau ? noteText : null,
          };
        }),
      };

      const res = await callApi<AssessmentSessionResponse>(
        `/whanau/${String(whanauId)}/assessment-sessions`,
        {
          method: "POST",
          body: JSON.stringify(body),
          headers: { "content-type": "application/json" },
        },
      );

      setSaveState({
        kind: "saved",
        sessionId: res.session_id,
        count: res.assessment_count,
      });
      setTouched(false);
      if (cycleLabel === "exit") {
        // Exit assessment: refresh the profile now but keep the modal open on
        // the QR panel, so the kaimahi can show the whānau their survey there
        // and then. They close it manually when done.
        onSaved();
      } else {
        // After a moment, refresh the underlying profile and close.
        setTimeout(() => {
          onSaved();
          onClose();
        }, 1200);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${String(err.status)} — ${err.message}`
          : err instanceof Error
            ? err.message
            : "Save failed";
      setSaveState({ kind: "error", message });
    }
  }, [
    whanauId,
    profile,
    cycleLabel,
    completedBy,
    sessionAt,
    setting,
    sessionNotes,
    supervisorOid,
    picks,
    callApi,
    onSaved,
    onClose,
  ]);

  const goPrev = () => {
    if (isFirstStep) return;
    setCurrentStepIndex((i) => i - 1);
  };
  const goNext = () => {
    if (isLastStep) return;
    setCurrentStepIndex((i) => i + 1);
  };

  // ---- Render -------------------------------------------------------------
  const overlay = (
    <div
      className="wizard-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-title"
      onMouseDown={(e) => {
        // Only close on backdrop click — not on clicks bubbling up from
        // inside the modal card.
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div className="wizard-modal">
        <header className="wizard-modal__header">
          <div className="wizard-modal__header-text">
            <h2 id="wizard-title" className="wizard-modal__title">
              {t("wizard.title", { name: profile.whanau.display_name })}
            </h2>
            <p className="wizard-modal__progress-text">
              {t("wizard.progress", {
                complete: completeCount,
                total: totalSubdomains,
              })}
            </p>
          </div>
          <button
            type="button"
            className="wizard-modal__close"
            onClick={requestClose}
            aria-label={t("wizard.close")}
          >
            ×
          </button>
        </header>

        <StepProgress
          steps={steps}
          currentStepIndex={currentStepIndex}
          completedSteps={completedSteps}
          onStepClick={(i) => setCurrentStepIndex(i)}
        />

        <section className="wizard-modal__body" aria-live="polite">
          {saveState.kind === "saved" && cycleLabel === "exit" ? (
            <ExitSurveyQr whanauId={whanauId} onClose={onClose} />
          ) : currentStep.kind === "setup" ? (
            <SetupCard
              completedBy={completedBy}
              onCompletedByChange={(c) => {
                setCompletedBy(c);
                setTouched(true);
              }}
              cycleLabel={cycleLabel}
              allowedCycles={submissionState.allowedCycles}
              onCycleChange={(c) => {
                setCycleLabel(c);
                setTouched(true);
              }}
              sessionAt={sessionAt}
              onSessionAtChange={(s) => {
                setSessionAt(s);
                setTouched(true);
              }}
              setting={setting}
              onSettingChange={(s) => {
                setSetting(s);
                setTouched(true);
              }}
              sessionNotes={sessionNotes}
              onSessionNotesChange={(s) => {
                setSessionNotes(s);
                setTouched(true);
              }}
              supervisorOid={supervisorOid}
              supervisors={supervisors}
              onSupervisorChange={(oid) => {
                setSupervisorOid(oid);
                setTouched(true);
              }}
              disabled={saveState.kind === "saving"}
            />
          ) : (
            <DomainCard
              domain={currentStep.domain}
              subdomains={subdomainsByDomain.get(currentStep.domain.id) ?? []}
              picks={picks}
              onPickChange={setPick}
              disabled={saveState.kind === "saving"}
            />
          )}
        </section>

        <WizardFooter
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          currentStepComplete={currentStepComplete}
          saveState={saveState}
          draftState={draftState}
          onPrev={goPrev}
          onNext={goNext}
          onSubmit={() => {
            void onSubmit();
          }}
          onSaveDraft={() => {
            void onSaveDraft();
          }}
        />
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

// ---------------------------------------------------------------------------
// Step progress indicator
// ---------------------------------------------------------------------------

function StepProgress({
  steps,
  currentStepIndex,
  completedSteps,
  onStepClick,
}: {
  steps: readonly StepKind[];
  currentStepIndex: number;
  completedSteps: ReadonlySet<number>;
  onStepClick: (i: number) => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <ol className="step-progress" aria-label={t("wizard.stepsLabel")}>
      {steps.map((step, i) => {
        const isCurrent = i === currentStepIndex;
        const isComplete = completedSteps.has(i);
        const label =
          step.kind === "setup"
            ? t("wizard.setup")
            : frameworkLabel(step.domain, "domain", i18n.language, t);
        return (
          <li
            key={step.kind === "setup" ? "setup" : step.domain.id}
            className={
              "step-progress__item" +
              (isCurrent ? " step-progress__item--current" : "") +
              (isComplete && !isCurrent ? " step-progress__item--complete" : "")
            }
          >
            <button
              type="button"
              onClick={() => onStepClick(i)}
              aria-current={isCurrent ? "step" : undefined}
              className="step-progress__button"
            >
              <span className="step-progress__number">
                {isComplete && !isCurrent ? "✓" : i + 1}
              </span>
              <span className="step-progress__label">{label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Setup card (step 0)
// ---------------------------------------------------------------------------

function SetupCard({
  completedBy,
  onCompletedByChange,
  cycleLabel,
  allowedCycles,
  onCycleChange,
  sessionAt,
  onSessionAtChange,
  setting,
  onSettingChange,
  sessionNotes,
  onSessionNotesChange,
  supervisorOid,
  supervisors,
  onSupervisorChange,
  disabled,
}: {
  completedBy: CompletionMode | "";
  onCompletedByChange: (c: CompletionMode) => void;
  cycleLabel: CycleLabel;
  allowedCycles: readonly CycleLabel[];
  onCycleChange: (c: CycleLabel) => void;
  sessionAt: string;
  onSessionAtChange: (s: string) => void;
  setting: string;
  onSettingChange: (s: string) => void;
  sessionNotes: string;
  onSessionNotesChange: (s: string) => void;
  supervisorOid: string;
  supervisors: Supervisor[];
  onSupervisorChange: (oid: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  // Filter CYCLE_LABELS down to ones the server will accept. Helper
  // in useSubmissionState.ts derives this from session history.
  const visibleCycles = CYCLE_LABELS.filter((c) =>
    allowedCycles.includes(c.id),
  );
  return (
    <>
      <h3 className="wizard__card-title">{t("wizard.setup")}</h3>
      <p className="wizard__card-subtitle">{t("wizard.setupSubtitle")}</p>

      <div className="wizard__setup-grid">
        <div className="wizard__field wizard__field--wide">
          <span className="wizard__field-label">
            {t("wizard.completedBy.label")}
          </span>
          <div
            className="wizard__cycle-options"
            role="radiogroup"
            aria-required="true"
          >
            {(["kaimahi", "whanau", "collaborative"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={completedBy === m}
                disabled={disabled}
                className={
                  completedBy === m
                    ? "wizard__cycle-button wizard__cycle-button--active"
                    : "wizard__cycle-button"
                }
                onClick={() => onCompletedByChange(m)}
              >
                {t(`wizard.completedBy.${m}`)}
              </button>
            ))}
          </div>
          {completedBy === "" && (
            <span className="wizard__field-hint">
              {t("wizard.completedBy.required")}
            </span>
          )}
        </div>

        <div className="wizard__field wizard__field--wide">
          <span className="wizard__field-label">{t("wizard.cycleLabel")}</span>
          <div className="wizard__cycle-options" role="radiogroup">
            {visibleCycles.map((c) => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={cycleLabel === c.id}
                disabled={disabled}
                className={
                  cycleLabel === c.id
                    ? "wizard__cycle-button wizard__cycle-button--active"
                    : "wizard__cycle-button"
                }
                onClick={() => onCycleChange(c.id)}
              >
                {t(`wizard.cycle.${c.id}`, { defaultValue: c.nameEn })}
              </button>
            ))}
          </div>
        </div>

        <div className="wizard__field">
          <label htmlFor="session-at" className="wizard__field-label">
            {t("wizard.sessionAt")}
          </label>
          <input
            id="session-at"
            type="datetime-local"
            value={sessionAt}
            onChange={(e) => onSessionAtChange(e.target.value)}
            disabled={disabled}
          />
        </div>

        <div className="wizard__field">
          <label htmlFor="setting" className="wizard__field-label">
            {t("wizard.setting")}
          </label>
          <input
            id="setting"
            type="text"
            value={setting}
            onChange={(e) => onSettingChange(e.target.value)}
            placeholder={t("wizard.settingPlaceholder")}
            disabled={disabled}
          />
        </div>

        <div className="wizard__field wizard__field--wide">
          <label htmlFor="session-notes" className="wizard__field-label">
            {t("wizard.sessionNotes")}{" "}
            <span className="wizard__field-hint">
              ({t("wizard.privateHint")})
            </span>
          </label>
          <textarea
            id="session-notes"
            value={sessionNotes}
            onChange={(e) => onSessionNotesChange(e.target.value)}
            placeholder={t("wizard.sessionNotesPlaceholder")}
            rows={3}
            disabled={disabled}
          />
        </div>

        <div className="wizard__field wizard__field--wide">
          <label htmlFor="supervisor" className="wizard__field-label">
            {t("wizard.supervisor.label")}
          </label>
          <select
            id="supervisor"
            className="wizard__select"
            value={supervisorOid}
            onChange={(e) => onSupervisorChange(e.target.value)}
            disabled={disabled}
            aria-required="true"
          >
            <option value="">{t("wizard.supervisor.placeholder")}</option>
            {supervisors.map((s) => (
              <option key={s.oid} value={s.oid}>
                {s.displayName ?? s.upn ?? s.oid}
              </option>
            ))}
          </select>
          {supervisorOid === "" && (
            <span className="wizard__field-hint">
              {t("wizard.supervisor.required")}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Domain card (steps 1..6)
// ---------------------------------------------------------------------------

function DomainCard({
  domain,
  subdomains,
  picks,
  onPickChange,
  disabled,
}: {
  domain: MergedDomain;
  subdomains: SubdomainAssessmentRow[];
  picks: ReadonlyMap<string, PickState>;
  onPickChange: (subdomainId: string, partial: Partial<PickState>) => void;
  disabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [openSubdomainId, setOpenSubdomainId] = useState<string | null>(null);

  const openSubdomain =
    openSubdomainId !== null
      ? (subdomains.find((s) => s.subdomain_id === openSubdomainId) ?? null)
      : null;

  return (
    <>
      <h3 className="wizard__card-title">
        {frameworkLabel(domain, "domain", i18n.language, t)}
      </h3>
      <p className="wizard__card-subtitle">
        {t("wizard.domainSubtitle", { count: subdomains.length })}
      </p>

      <ul className="wizard__subdomains">
        {subdomains.map((a) => (
          <SubdomainTile
            key={a.subdomain_id}
            subdomain={a}
            pick={picks.get(a.subdomain_id) ?? null}
            /* Baseline for the trajectory indicator. a.state_kind is the
             * most-recent persisted state — the value the kaimahi is
             * comparing against as they enter the new cycle. Null when
             * the subdomain has never been assessed. */
            baselineState={(a.state_kind as FactorStateKind | null) ?? null}
            disabled={disabled}
            onOpen={() => setOpenSubdomainId(a.subdomain_id)}
          />
        ))}
      </ul>

      {openSubdomain && (
        <SubdomainPickerModal
          subdomain={openSubdomain}
          pick={picks.get(openSubdomain.subdomain_id) ?? null}
          disabled={disabled}
          onChange={(partial) =>
            onPickChange(openSubdomain.subdomain_id, partial)
          }
          onCommitAndClose={() => setOpenSubdomainId(null)}
          onClose={() => setOpenSubdomainId(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Subdomain tile — clickable card on the 3-col grid. Shows current pick.
// ---------------------------------------------------------------------------

function SubdomainTile({
  subdomain,
  pick,
  baselineState,
  disabled,
  onOpen,
}: {
  subdomain: SubdomainAssessmentRow;
  pick: PickState | null;
  /** The most-recent persisted state for this subdomain, used as the
   * "before" side of the trajectory indicator. Null for first-ever
   * assessment of this subdomain. */
  baselineState: FactorStateKind | null;
  disabled: boolean;
  onOpen: () => void;
}) {
  const { t, i18n } = useTranslation();
  // Resolve display name via the merged framework so admin renames apply
  // here too. Falls back to the server-side row name if the subdomain
  // isn't in the merged map (shouldn't happen — defensive).
  const { findSubdomain } = useFramework();
  const merged = findSubdomain(subdomain.subdomain_id);
  const displayName = merged
    ? frameworkLabel(merged, "subdomain", i18n.language, t)
    : subdomain.name_en;

  const stateKind = pick?.state_kind ?? null;
  const reason = pick?.not_assessed_reason_id ?? null;
  const incomplete = stateKind === "not-assessed" && !reason;
  const hasNote = (pick?.note ?? "").trim().length > 0;

  const stateLabel = stateKind ? FACTOR_STATES[stateKind] : null;

  // Trajectory: compare baseline (last persisted) against current pick.
  // Reuses the share-view's score ordering: protective(3) > change(2) >
  // risk(1) > not-assessed(0). null baseline = first-ever assessment.
  const trajectory = computeTrajectory(baselineState, stateKind);

  return (
    <li className="wizard__tile-wrap">
      <button
        type="button"
        className={`wizard__tile wizard__tile--${stateKind ?? "unset"}${
          incomplete ? " wizard__tile--incomplete" : ""
        }`}
        onClick={onOpen}
        disabled={disabled}
        aria-label={t("wizard.tileAria", { subdomain: displayName })}
      >
        <span className="wizard__tile-name">{displayName}</span>
        {stateLabel ? (
          <span
            className={`wizard__tile-pill wizard__tile-pill--${stateKind ?? "unset"}`}
          >
            {stateLabel.teReoName ?? stateLabel.nameEn}
            {stateLabel.teReoName && (
              <span className="wizard__tile-pill-en">{stateLabel.nameEn}</span>
            )}
          </span>
        ) : (
          <span className="wizard__tile-pill wizard__tile-pill--unset">
            {t("wizard.tilePickPrompt")}
          </span>
        )}
        {trajectory && (
          <span
            className={`wizard__tile-trajectory wizard__tile-trajectory--${trajectory.direction}`}
            aria-label={t(trajectory.ariaKey, {
              from: t(`wizard.trajectory.label.${trajectory.fromKey}`),
              to: t(`wizard.trajectory.label.${trajectory.toKey}`),
            })}
          >
            <span className="wizard__tile-trajectory-icon" aria-hidden="true">
              {trajectory.glyph}
            </span>
            <span className="wizard__tile-trajectory-text">
              {t(trajectory.shortKey, {
                to: t(`wizard.trajectory.label.${trajectory.toKey}`),
              })}
            </span>
          </span>
        )}
        {hasNote && (
          <span
            className="wizard__tile-note-marker"
            aria-label={t("wizard.tileHasNote")}
            title={t("wizard.tileHasNote")}
          >
            ✎
          </span>
        )}
        {incomplete && (
          <span className="wizard__tile-incomplete">
            {t("profile.notAssessedNeedsReason")}
          </span>
        )}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Trajectory helper — compares baseline (last saved) vs current pick.
// ---------------------------------------------------------------------------

type TileTrajectory = {
  /** Used for the CSS modifier on the trajectory pill. */
  direction: "up" | "down" | "unchanged" | "new" | "now-na" | "newly-assessed";
  glyph: string;
  fromKey: "risk" | "change" | "protective" | "notAssessed" | "none";
  toKey: "risk" | "change" | "protective" | "notAssessed";
  /** Full i18n key for the visible short label inside the tile. */
  shortKey: string;
  /** Full i18n key for the (longer) aria-label read by screen readers. */
  ariaKey: string;
};

const STATE_SCORE: Record<FactorStateKind, number> = {
  risk: 1,
  change: 2,
  protective: 3,
  "not-assessed": 0,
};

function stateKey(k: FactorStateKind): TileTrajectory["toKey"] {
  return k === "not-assessed" ? "notAssessed" : k;
}

function computeTrajectory(
  baseline: FactorStateKind | null,
  current: FactorStateKind | null,
): TileTrajectory | null {
  if (!current) return null;
  const toKey = stateKey(current);
  // First-ever assessment for this subdomain.
  if (!baseline) {
    return {
      direction: "new",
      glyph: "✦",
      fromKey: "none",
      toKey,
      shortKey: "wizard.trajectory.new",
      ariaKey: "wizard.trajectory.new",
    };
  }
  if (baseline === current) {
    return {
      direction: "unchanged",
      glyph: "→",
      fromKey: stateKey(baseline),
      toKey,
      shortKey: "wizard.trajectory.unchanged",
      ariaKey: "wizard.trajectory.unchanged",
    };
  }
  // Special-case transitions involving 'not-assessed' — a numeric compare
  // would mislabel them (e.g. protective→not-assessed isn't a "decline").
  if (current === "not-assessed") {
    return {
      direction: "now-na",
      glyph: "◌",
      fromKey: stateKey(baseline),
      toKey,
      shortKey: "wizard.trajectory.nowNaShort",
      ariaKey: "wizard.trajectory.nowNaShort",
    };
  }
  if (baseline === "not-assessed") {
    return {
      direction: "newly-assessed",
      glyph: "✦",
      fromKey: stateKey(baseline),
      toKey,
      shortKey: "wizard.trajectory.newlyAssessedShort",
      ariaKey: "wizard.trajectory.newlyAssessedShort",
    };
  }
  const diff = STATE_SCORE[current] - STATE_SCORE[baseline];
  return {
    direction: diff > 0 ? "up" : "down",
    glyph: diff > 0 ? "↑" : "↓",
    fromKey: stateKey(baseline),
    toKey,
    shortKey:
      diff > 0 ? "wizard.trajectory.upShort" : "wizard.trajectory.downShort",
    ariaKey: diff > 0 ? "wizard.trajectory.up" : "wizard.trajectory.down",
  };
}

// ---------------------------------------------------------------------------
// Subdomain picker modal — opens on tile click. Notes above, state buttons
// below. Picking a state commits + closes; picking "Not assessed" expands
// to reason buttons, and picking a reason commits + closes.
// ---------------------------------------------------------------------------

function SubdomainPickerModal({
  subdomain,
  pick,
  disabled,
  onChange,
  onCommitAndClose,
  onClose,
}: {
  subdomain: SubdomainAssessmentRow;
  pick: PickState | null;
  disabled: boolean;
  onChange: (partial: Partial<PickState>) => void;
  /** Called when a state (or not-assessed reason) is committed; auto-closes. */
  onCommitAndClose: () => void;
  /** Close without picking — backdrop, ESC, or X button. */
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  // Resolve display name via the merged framework — picks up admin renames.
  const { findSubdomain } = useFramework();
  const merged = findSubdomain(subdomain.subdomain_id);
  const displayName = merged
    ? frameworkLabel(merged, "subdomain", i18n.language, t)
    : subdomain.name_en;

  const note = pick?.note ?? "";
  const shareWithWhanau = pick?.share_with_whanau ?? false;
  const stateKind = pick?.state_kind ?? null;
  const reason = pick?.not_assessed_reason_id ?? null;

  // When the user picks "Not assessed", show the reason picker before
  // committing. Tracked locally so picking "Not assessed" doesn't
  // immediately auto-close — they still need to pick a reason. On open,
  // default to reasons view only if not-assessed is picked but no reason
  // recorded yet; otherwise show the state buttons so the kaimahi can
  // change their pick freely.
  const [pendingNotAssessed, setPendingNotAssessed] = useState(
    stateKind === "not-assessed" && !reason,
  );
  const showingReasons = pendingNotAssessed;

  const canShare = note.trim().length > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function pickState(kind: FactorStateKind) {
    if (kind === "not-assessed") {
      // First click on "Not assessed" — expand to reasons rather than commit.
      setPendingNotAssessed(true);
      onChange({ state_kind: "not-assessed" });
      return;
    }
    onChange({ state_kind: kind, not_assessed_reason_id: null });
    onCommitAndClose();
  }

  function pickReason(r: NotAssessedReason) {
    onChange({ state_kind: "not-assessed", not_assessed_reason_id: r });
    onCommitAndClose();
  }

  function reasonI18nKey(r: NotAssessedReason): string {
    switch (r) {
      case "not-applicable":
        return "notApplicable";
      case "declined":
        return "declined";
      case "not-yet-assessed":
        return "notYetAssessed";
    }
  }

  return createPortal(
    <div
      className="wizard-picker__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="wizard-picker" role="dialog" aria-modal="true">
        <header className="wizard-picker__header">
          <h3 className="wizard-picker__title">{displayName}</h3>
          <button
            type="button"
            className="wizard-picker__close"
            onClick={onClose}
            aria-label={t("wizard.close")}
          >
            ×
          </button>
        </header>

        <div className="wizard-picker__body">
          {/* State buttons at the top — primary action. Picking a state
              (other than Not assessed) commits + closes immediately. */}
          {!showingReasons ? (
            <section
              className="wizard-picker__states"
              role="group"
              aria-label={t("preview.statePickerLabel", {
                subdomain: displayName,
              })}
            >
              {(["risk", "change", "protective", "not-assessed"] as const).map(
                (kind) => {
                  const fs = FACTOR_STATES[kind];
                  const isCurrent = stateKind === kind;
                  return (
                    <button
                      key={kind}
                      type="button"
                      className={`wizard-picker__state wizard-picker__state--${kind}${
                        isCurrent ? " wizard-picker__state--current" : ""
                      }`}
                      onClick={() => pickState(kind)}
                      disabled={disabled}
                      aria-pressed={isCurrent}
                    >
                      <span className="wizard-picker__state-icon">
                        <StateIcon state={kind} />
                      </span>
                      <span className="wizard-picker__state-labels">
                        {fs.teReoName && (
                          <span className="wizard-picker__state-mi">
                            {fs.teReoName}
                          </span>
                        )}
                        <span className="wizard-picker__state-en">
                          {fs.nameEn}
                        </span>
                      </span>
                    </button>
                  );
                },
              )}
            </section>
          ) : (
            <section
              className="wizard-picker__reasons"
              role="group"
              aria-label={t("preview.reasonPickerLabel", {
                subdomain: displayName,
              })}
            >
              <p className="wizard-picker__reasons-prompt">
                {t("wizard.notAssessedReasonPrompt")}
              </p>
              {NOT_ASSESSED_REASONS.map((r) => {
                const isCurrent = reason === r;
                return (
                  <button
                    key={r}
                    type="button"
                    className={`wizard-picker__reason${
                      isCurrent ? " wizard-picker__reason--current" : ""
                    }`}
                    onClick={() => pickReason(r)}
                    disabled={disabled}
                    aria-pressed={isCurrent}
                  >
                    {t(`framework.notAssessedReason.${reasonI18nKey(r)}`)}
                  </button>
                );
              })}
              <button
                type="button"
                className="wizard-picker__reasons-back"
                onClick={() => setPendingNotAssessed(false)}
                disabled={disabled}
              >
                ← {t("wizard.backToStates")}
              </button>
            </section>
          )}

          {/* Notes section at the bottom — secondary. Changes save as you
              type via onChange, so closing the modal preserves them. */}
          <section className="wizard-picker__notes">
            <label
              htmlFor={`picker-note-${subdomain.subdomain_id}`}
              className="wizard-picker__notes-label"
            >
              {t("wizard.noteLabel")}
            </label>
            <textarea
              id={`picker-note-${subdomain.subdomain_id}`}
              value={note}
              onChange={(e) => onChange({ note: e.target.value })}
              placeholder={t("wizard.notePlaceholder")}
              rows={3}
              disabled={disabled}
              className="wizard-picker__notes-textarea"
            />
            <button
              type="button"
              className="wizard-picker__share-toggle"
              aria-pressed={shareWithWhanau}
              onClick={() => onChange({ share_with_whanau: !shareWithWhanau })}
              disabled={disabled || !canShare}
              title={
                canShare
                  ? shareWithWhanau
                    ? t("wizard.shareOn")
                    : t("wizard.shareOff")
                  : t("wizard.shareNeedsNote")
              }
            >
              <span aria-hidden="true" className="wizard-picker__share-glyph">
                {shareWithWhanau ? "✓" : ""}
              </span>
              {t("wizard.shareWithWhanau")}
            </button>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function WizardFooter({
  isFirstStep,
  isLastStep,
  currentStepComplete,
  saveState,
  draftState,
  onPrev,
  onNext,
  onSubmit,
  onSaveDraft,
}: {
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepComplete: boolean;
  saveState:
    | { kind: "idle" }
    | { kind: "saving" }
    | { kind: "saved"; sessionId: number; count: number }
    | { kind: "error"; message: string };
  draftState:
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "restored"; at: string }
    | { kind: "saving-draft" }
    | { kind: "saved-draft" }
    | { kind: "draft-error"; message: string };
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
}) {
  const { t } = useTranslation();
  const submitting = saveState.kind === "saving" || saveState.kind === "saved";
  const savingDraft = draftState.kind === "saving-draft";

  return (
    <footer className="wizard-modal__footer">
      <button
        type="button"
        className="auth-button auth-button--secondary"
        onClick={onPrev}
        disabled={isFirstStep || saveState.kind === "saving"}
      >
        ← {t("wizard.previous")}
      </button>

      <div className="wizard__footer-status">
        {saveState.kind === "saved" && (
          <p className="wizard__saved-toast">
            {t("wizard.savedToast", { count: saveState.count })}
          </p>
        )}
        {saveState.kind === "error" && (
          <p className="wizard__error-toast">
            {t("wizard.saveError", { message: saveState.message })}
          </p>
        )}
        {draftState.kind === "restored" && saveState.kind === "idle" && (
          <p className="wizard__draft-toast">
            {t("wizard.draftRestored", {
              at: new Date(draftState.at).toLocaleString(),
            })}
          </p>
        )}
        {draftState.kind === "saved-draft" && saveState.kind === "idle" && (
          <p className="wizard__draft-toast">{t("wizard.draftSaved")}</p>
        )}
        {draftState.kind === "draft-error" && (
          <p className="wizard__error-toast">
            {t("wizard.draftError", { message: draftState.message })}
          </p>
        )}
        {!currentStepComplete &&
          saveState.kind === "idle" &&
          draftState.kind !== "saved-draft" &&
          draftState.kind !== "restored" && (
            <p className="wizard__incomplete-hint">
              {t("wizard.completeThisStep")}
            </p>
          )}
      </div>

      <button
        type="button"
        className="auth-button auth-button--secondary wizard__save-draft"
        onClick={onSaveDraft}
        disabled={submitting || savingDraft}
        title={t("wizard.saveDraftHint")}
      >
        {savingDraft ? t("wizard.savingDraft") : t("wizard.saveDraft")}
      </button>

      {isLastStep ? (
        <button
          type="button"
          className="auth-button auth-button--primary"
          onClick={onSubmit}
          disabled={!currentStepComplete || submitting}
        >
          {saveState.kind === "saving"
            ? t("wizard.submitting")
            : t("wizard.submit")}
        </button>
      ) : (
        <button
          type="button"
          className="auth-button auth-button--primary"
          onClick={onNext}
          disabled={!currentStepComplete}
        >
          {t("wizard.next")} →
        </button>
      )}
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isoLocalNow(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default AssessmentWizard;
